import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import Loading from '../Loading';
import styleClasses from './css/MyPage.module.scss';

function SourceSelector({ onSelect }) {
  const [albumItems, setAlbumItems] = useState([]);     // 앨범 목록
  const [trackItems, setTrackItems] = useState([]);       // 선택된 앨범의 트랙 목록
  const [activityItems, setActivityItems] = useState([]); // Activity 목록
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null); // 선택된 앨범
  const navigate = useNavigate();
  // 두 가지 파라미터 추출: positionId와 memberID
  const { positionId, memberID, musicardID } = useParams();
  const location = useLocation();
  const { skillIndex } = location.state || {}; // 부모에서 전달한 skillIndex (포지션용)
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  // 처음에는 앨범과 Activity 목록만 불러오기
  useEffect(() => {
    if (!uid) {
      navigate('/login');
      return;
    }
    const fetchAlbumsAndActivities = async () => {
      setIsLoading(true);
      try {
        let albums = [];
        const attrs = ["released", "unreleased"];
        for (const attr of attrs) {
          const albumsRef = collection(db, "users_data", uid, attr);
          const albumsSnapshot = await getDocs(albumsRef);
          albumsSnapshot.docs.forEach((albumDoc) => {
            const albumData = albumDoc.data();
            const albumId = albumDoc.id;
            albums.push({
              id: `${attr}-${albumId}`, // 고유 키 생성
              albumId,
              attribute: attr,
              albumTitle: albumData.title || albumData.name || "앨범 제목 없음",
              cover: albumData.cover || "", // 앨범 커버
            });
          });
        }
        const activityRef = collection(db, "users_data", uid, "activity");
        const activitySnapshot = await getDocs(activityRef);
        const activities = activitySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAlbumItems(albums);
        setActivityItems(activities);
      } catch (error) {
        console.error("아이템 가져오기 오류:", error);
      }
      setIsLoading(false);
    };

    fetchAlbumsAndActivities();
  }, [uid, navigate]);

  // 앨범 선택 시, 해당 앨범의 트랙들을 불러오기
  useEffect(() => {
    if (selectedAlbum) {
      const fetchTracks = async () => {
        setIsLoading(true);
        try {
          const { attribute: attr, albumId } = selectedAlbum;
          const tracksRef = collection(db, "users_data", uid, attr, albumId, "tracks");
          const tracksSnapshot = await getDocs(tracksRef);
          let tracks = [];
          tracksSnapshot.docs.forEach((trackDoc, index) => {
            tracks.push({
              id: `${attr}-${albumId}-${trackDoc.id}-${index}`, // 중복 방지를 위해 index 추가
              ...trackDoc.data(),
            });
          });
          setTrackItems(tracks);
        } catch (error) {
          console.error("트랙 가져오기 오류:", error);
        }
        setIsLoading(false);
      };
      fetchTracks();
    }
  }, [selectedAlbum, uid]);

  // 선택한 항목(트랙) 처리 (Archive)
  const handleTrackSelect = (track) => {
    const result = {
      url: track.url || "",
      cover: selectedAlbum.cover || "",
      title: track.title || "", // 🔹 트랙 제목 추가
    };
    if (onSelect && typeof onSelect === "function") {
      onSelect(result, skillIndex);
    } else {
      if (memberID) {
        navigate(`/my-page/members/${memberID}`, { state: { selectedSource: result } });
      } else if (positionId) {
        navigate(`/my-page/position-skill/${positionId}`, { state: { selectedSource: result, skillIndex } });
      } else if (musicardID) {
        navigate(`/my-page/musicard/${musicardID}`, { state: { selectedSource: result, skillIndex } });
      }
    }
  };

  // Activity 항목 선택 시 처리
  const handleActivitySelect = (item) => {
    let result = {};
    if (item.channelType === "YouTube") {
      result = { videoId: item.videoId || "" };
    } else {
      result = {
        link: item.link || "",
        photo: item.photo || ""
      };
    }
    if (onSelect && typeof onSelect === "function") {
      onSelect(result, skillIndex);
    } else {
      if (memberID) {
        navigate(`/my-page/members/${memberID}`, { state: { selectedSource: result, skillIndex } });
      } else if (positionId) {
        navigate(`/my-page/position-skill/${positionId}`, { state: { selectedSource: result, skillIndex } });
      } else if (musicardID) {
        navigate(`/my-page/musicard/${musicardID}`, { state: { selectedSource: result, skillIndex } });
      }
    }
  };

  if (isLoading) return <Loading />;

  return (
    <div className={styleClasses.skillSourceSelector}>
      <h1>소스 선택</h1>
      {selectedAlbum ? (
        <div>
          <button 
            onClick={() => { setSelectedAlbum(null); setTrackItems([]); }}
            style={{ marginBottom: "1rem" }}
          >
            ← 앨범 목록으로 돌아가기
          </button>
          <h2>앨범: {selectedAlbum.albumTitle}</h2>
          <div>
            <h3>트랙 목록</h3>
            {trackItems.length > 0 ? (
              trackItems.map((track, index) => (
                <div 
                  key={`${track.id}-${index}`}
                  className={styleClasses.sourceItem}
                  onClick={() => handleTrackSelect(track)}
                  style={{ cursor: 'pointer', border: '1px solid #ccc', padding: '0.5rem', marginBottom: '0.5rem' }}
                >
                  <span>{track.name || track.title || "트랙"}</span>
                </div>
              ))
            ) : (
              <p>트랙이 없습니다.</p>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "2rem" }}>
          <div className={styleClasses.column}>
            <h2>앨범 목록</h2>
            {albumItems.length > 0 ? (
              albumItems.map((album, index) => (
                <div 
                  key={`${album.id}-${index}`}
                  className={styleClasses.sourceItem}
                  onClick={() => setSelectedAlbum(album)}
                  style={{ cursor: 'pointer', border: '1px solid #ccc', padding: '0.5rem', marginBottom: '0.5rem' }}
                >
                  {album.cover && (
                    <img 
                      src={album.cover} 
                      alt={album.albumTitle} 
                      style={{ width: '50px', height: '50px', marginRight: '0.5rem' }} 
                    />
                  )}
                  <span>{album.albumTitle || "앨범"}</span>
                </div>
              ))
            ) : (
              <p>앨범이 없습니다.</p>
            )}
          </div>
          <div className={styleClasses.column}>
            <h2>Activity 목록</h2>
            {activityItems.length > 0 ? (
              activityItems.map((item, index) => (
                <div 
                  key={`${item.id}-${index}`}
                  className={styleClasses.sourceItem}
                  onClick={() => handleActivitySelect(item)}
                  style={{ cursor: 'pointer', border: '1px solid #ccc', padding: '0.5rem', marginBottom: '0.5rem' }}
                >
                  {item.icon && (
                    <img 
                      src={item.icon} 
                      alt={item.title || item.name} 
                      style={{ width: '50px', height: '50px', marginRight: '0.5rem' }} 
                    />
                  )}
                  <span>{item.title || item.name || "Activity Item"}</span>
                </div>
              ))
            ) : (
              <p>Activity 아이템이 없습니다.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SourceSelector;